import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipInputEvent, MatChipOption } from '@angular/material/chips';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { LookupList, LookupListItem } from '@common/data-models/complex-types/lookup-list';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { OPTION_BLANK } from '@src/suite/common/enums/constants';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NavigationActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { from, Observable } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'st-tags-autocomplete',
    templateUrl: './tags-autocomplete.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./tags-autocomplete.scss'],
    host: {
        class: 'st-tags-autocomplete',
    },
})
export class TagsAutocompleteComponent extends EnpsControlComponent<LookupList> {
    value = input<LookupList>(new LookupList());

    maxItemsToShow = input(999, {
        transform: val => coerceNumberProperty(val, 999),
    });
    includeBlank = input(false, {
        transform: coerceBooleanProperty,
    });

    menuTrigger = viewChild(MatMenuTrigger);

    internalValue = signal<LookupList>(new LookupList());
    tagsFilter = new FormControl('');

    selectionList = viewChild(MatSelectionList);
    addTypeChip = viewChild(MatChipOption);

    items = computed(() => {
        if (this.tagsToShow?.length > 0) {
            return this.tagsToShow;
        }
        return null;
    });

    tagsToShow = computed(() => {
        return this.internalValue()?.slice(0, this.maxItemsToShow());
    });

    tagsAsTooltip = computed(() => {
        return this.internalValue()
            ?.slice(this.maxItemsToShow())
            .map(item => item.displayText)
            .join('\n');
    });

    shouldShowTagsAsTooltipChip = computed(() => {
        return this.maxItemsToShow() > 0 && this.internalValue()?.length > this.maxItemsToShow();
    });

    tagsAsTooltipChip = computed(() => {
        return `+ ${this.internalValue()?.length - this.maxItemsToShow()} more`;
    });

    addTagLabel = computed(() => this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebAddTagLabel))());

    constructor() {
        super();
        effect(
            () => {
                const val = this.value();
                const list = new LookupList();
                if (val instanceof LookupList) {
                    this.internalValue.set(val);
                }
                if (Array.isArray(val)) {
                    val.forEach(item => {
                        list.set(item);
                    });
                    this.internalValue.set(list);
                } else {
                    this.internalValue.set(list);
                }
            },
            { allowSignalWrites: true }
        );
    }

    suggestions: Observable<LookupListItem[]>;

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue());
        }
        this.tagsFilter.setValue('');
    }

    onMenuOpened() {
        super.onMenuOpened();
        this.suggestions = this.tagsFilter.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            switchMap(value => this.getSuggestions(value || '')),
            takeUntil(this.destroyed$)
        );
    }

    // TODO Maybe in store?
    public getSuggestions(val: string): Observable<LookupListItem[]> {
        return from(this.appService.nomWebServicesJson.getSuggestions(val)).pipe(
            map(res => {
                if (this.includeBlank()) {
                    return [
                        new LookupListItem(OPTION_BLANK, this.languageSettings.PSTWebBlankOption, this.languageSettings.PSTWebBlankOption),
                        ...res.data.map(item => new LookupListItem(item, item, item)),
                    ];
                }
                return res.data.map(item => new LookupListItem(item, item, item));
            })
        );
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        const lookupListItem = new LookupListItem(value, value, value);

        if (value && !this.valueContains(lookupListItem)) {
            this.internalValue.update(list => {
                list.set(lookupListItem);
                return list;
            });
            event.chipInput!.clear();
            this.tagsFilter.setValue('');
            this.isValueChanged.set(true);
        }
        this.menuTrigger().closeMenu();
        this.addTypeChip()?.focus();
    }

    selected(event: MatSelectionListChange): void {
        const item = event.options[0]?.value;
        const lookupListItem = new LookupListItem(item.id, item.displayText, item.description);
        if (item && item.id && !this.valueContains(lookupListItem)) {
            this.internalValue.update(list => {
                list.set(lookupListItem);
                return list;
            });
            this.searchBox().nativeElement.value = '';
            this.tagsFilter.setValue('');
            this.isValueChanged.set(true);
        }
        this.menuTrigger().closeMenu();
        this.addTypeChip()?.focus();
    }

    remove(tag: LookupListItem): void {
        const index = this.internalValue().findIndex(item => item.id === tag.id);

        if (index >= 0) {
            this.internalValue.update(list => {
                list.splice(index, 1);
                return list;
            });
        }
        this.isValueChanged.set(true);
        this.valueChanged();
    }

    search(tag: LookupListItem) {
        if (tag && tag.id) {
            const searchTerm: string = `#${tag.id.trim()}`;
            // TODO THIS NEEDS TO GO IN THE EFFECT
            const currentApp = this.store.selectSignal(selectCurrentApplication)();
            if (this.appService.appSupportMap[currentApp].supportsSearchMode) {
                this.store.dispatch(NavigationActions.enterSearchMode({ term: searchTerm }));
            }
        }
    }

    valueContains(tag: LookupListItem): boolean {
        return this.internalValue()?.findIndex(item => item.id === tag.id) >= 0;
    }

    onTabOrEscPress() {
        this.addTypeChip().focus();
    }

    openMatMenu() {
        if (!this.menuTrigger().menuOpen) {
            this.menuTrigger().openMenu();
            this.selectionList()?.options?.first?.focus();
        }
    }

    onUpArrowPress() {
        this.selectionList()?.options?.last?.focus();
    }

    onDownArrowPress() {
        this.selectionList()?.options?.first?.focus();
    }
}
